<template lang="pug">
.picture-table-component
  // テーブル
  b-table(
    :items="pictureItems"
    :fields="pictureFields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    // フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    // セル：利用者名
    template(#cell(residentName)="row")
      span.resident(:id="`resident-name-${row.item.id}`") {{ row.item.residentName }}
      b-popover(v-if="row.item.residentName" :target="`resident-name-${row.item.id}`" triggers="hover" :placement="'bottomright'")
        .d-flex.flex-column
          div.font-weight-bold 利用者グループ
          div {{ row.item.groupName }}
          div.font-weight-bold 部屋名
          div {{ row.item.residentRoom}}
          div.font-weight-bold 利用者名
          div {{ row.item.residentName }}
    // セル：配布
    template(#cell(distribute)="row")
      .state(v-if="row.item.distributedDatetime")
        .distributed-text 配布済
      button.btn.distribute-btn(v-if="!row.item.distributedDatetime" @click="distribute(row.item)") 未配布
    // セル：プレビュー
    template(#cell(preview)="row")
      b-spinner.spinner(v-if="row.item.convertedImageUrl == ''")
      img(v-else @click="showPicture(row.item)" :src="row.item.convertedImageUrl")
    // セル：印刷
    template(#cell(operation)="row")
      button.btn(@click="print(row.item)") 印刷

  .table-options.d-flex.flex-row.justify-content-end.align-items-center
    // 検索
    .search-filter.d-flex.mr-1.align-items-center.mr-2
      b-input-group.search-filter-input-group.mr-1(size="md")
        b-input-group-prepend
          b-input-group-text.search-filter-icon
            .search-icon
        b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
      HelpIcon.d-flex.justify-content-end.align-items-center(:popoverId="'picture-list-' + distributed + '-search-filter-help'" title="検索項目について")
        template(v-slot:text)
          div 検索項目は以下になります。
          div ・部屋名
          div ・利用者名
          div ・利用者名（ふりがな）※
          div ・依頼者名
          div ※ 利用者情報にふりがなを登録前の写真印刷依頼はふりがな検索ができません。
    // フィルタリング
    span.table-option-text 利用者グループ:
    .filter
      b-form-select(
        id="filter-input"
        v-model="filter.groupId"
        :options="filterOptions"
        size="md"
      )
    // ページあたりの行数
    span.table-option-text 1ページの行数:
    .per-page
      b-form-select(
        id="per-page-select"
        v-model="perPage"
        :options="pageOptions"
        size="md"
        @change="selectPerPage"
      )
    // ページネーション
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :disabled="isLoading"
      @change="selectPage"
      hide-goto-end-buttons
    )
  ImageModal(:imageUrl="showImageUrl" ref="imageModal")
</template>

<script lang="ts">
import { Component, Inject, Prop, Watch, Vue } from "vue-property-decorator";
import { userStore, itemListFilterStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import PicturesService, { PicturesGetModel } from "@/commons/services/Pictures";
import S3UrlService from "@/commons/services/S3Url";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";

import HelpIcon from "@/pc/basics/HelpIcon.vue";
import ImageModal from "@/pc/components/modals/ImageModal.vue";
import SystemError from "@/commons/models/SystemError";

/**
 * 写真テーブルコンポーネント
 */
@Component({
  components: {
    HelpIcon,
    ImageModal
  }
})
export default class PictureTable extends Vue {
  /** 写真サービス */
  @Inject("PicturesService") picturesService!: PicturesService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /**
   * 配布・未配布の絞り込み設定
   */
  @Prop({
    default: () => {
      return undefined;
    }
  })
  distributed!: boolean;

  @Prop()
  settingNameSuffix!: string;

  /** 写真列名 */
  pictureFields: Array<PictureTableField> = [];
  /** 写真各行の情報 */
  pictureItems: Array<PictureTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** モーダルで表示する画像URL */
  showImageUrl: string = "";
  /** 利用者グループMAP */
  groupIdMap: Map<number, ResidentGroupsGetModel> = new Map<number, ResidentGroupsGetModel>();

  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "", groupId: "" };
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.picture.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.picture.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.picture.pageRange;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;

    let cookieName: string = "";
    if (this.distributed != undefined) {
      cookieName = `picture-list-${String(this.distributed)}-per-page=${perPage}`;
    } else {
      cookieName = `picture-list-per-page=${perPage}`;
    }

    document.cookie = `${cookieName}; max-age=${saveSecond};`;

    return;
  }

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const pictureFields: Array<PictureTableField> = this.createPictureFields();
    this.pictureFields = pictureFields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.picture.perPage);

    // グループIDとグループ名の変換用にグループ情報のMAPを作る
    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const groupIdMap: Map<number, ResidentGroupsGetModel> = this.createGroupIdMap(groups);
    this.groupIdMap = groupIdMap;
    const filterOptions: Array<{ value: string; text: string }> = await this.createFilterOptions(groups);
    this.filterOptions = filterOptions;
    // フィルタの復元
    this.restoreFilterSetting();

    // 動的に変更する画面情報を作成する
    this.pictureItems = [];
    this.currentRows = this.pictureItems.length;

    await this.createView();

    return;
  }

  /**
   * ### 写真の列名を作成する
   */
  public createPictureFields(): Array<PictureTableField> {
    let fields: Array<PictureTableField> = [];

    fields.push({ key: "distribute", label: "配布", sortable: false, sortDirection: "desc", class: "distribute text-center" });
    fields.push({ key: "receiveDatetime", label: "受付日時", sortable: false, sortDirection: "desc", class: "receive-datetime text-center" });
    fields.push({ key: "distributedDatetime", label: "配布完了日時", sortable: false, sortDirection: "desc", class: "distributed-datetime text-center" });
    fields.push({ key: "residentRoom", label: "部屋名", sortable: false, sortDirection: "desc", class: "resident-room" });
    fields.push({ key: "residentName", label: "利用者名", sortable: false, sortDirection: "desc", class: "resident-name" });
    fields.push({ key: "familyName", label: "依頼者名", sortable: false, sortDirection: "desc", class: "family-name" });
    fields.push({ key: "preview", label: "プレビュー", sortable: false, sortDirection: "desc", class: "preview text-center" });
    fields.push({ key: "comment", label: "メッセージ", sortable: false, sortDirection: "desc", class: "comment" });
    fields.push({ key: "operation", label: "写真", sortable: false, sortDirection: "desc", class: "operation text-center" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      let cookieName: string = "";
      if (this.distributed != undefined) {
        cookieName = `picture-list-${String(this.distributed)}-per-page`;
      } else {
        cookieName = `picture-list-per-page`;
      }
      const perPage: number = cookieMap[cookieName];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループマスターのMAPを作る
   */
  public createGroupIdMap(residentGroups: Array<ResidentGroupsGetModel>): Map<number, ResidentGroupsGetModel> {
    let map: Map<number, ResidentGroupsGetModel> = new Map<number, ResidentGroupsGetModel>();
    for (const group of residentGroups) {
      map.set(group.id, group);
    }

    return map;
  }

  /**
   * ### 利用者グループフィルター用選択項目
   *
   * - アカウント情報から許可されている利用者グループIDの配列を取得する
   * - 利用者グループマスターと照合して選択項目の利用者グループを作成する
   */
  public createFilterOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: string; text: string }> {
    const filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        filterOptions.push({
          value: `${group.id}`,
          text: group.name
        });
      }
    }

    return filterOptions;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name + `-${this.settingNameSuffix}`] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name + `-${this.settingNameSuffix}`];
    }
    return;
  }

  /**
   * 画面情報を作成する
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 全体の行数を非同期で取得する
    this.setRows();

    // 写真テーブルに渡す各プロパティを作成する
    const pictures: Array<PicturesGetModel> = await this.getPictures();
    const pictureItems: Array<PictureTableItem> = this.createPictureItems(pictures, this.groupIdMap);
    this.pictureItems = this.pictureItems.concat(pictureItems);
    this.currentRows = this.pictureItems.length;

    this.isLoading = false;

    // 画面表示後、画像URLを変換する
    // DBのスループット上限問題、chromeのリクエスト上限を回避するために50件ずつ取得し、取得する毎に1秒インターバルを挟む
    let promises: Array<Promise<void>> = [];
    for (let i = 0; i < this.pictureItems.length; i++) {
      promises.push(this.convertImage(i));

      if (Setting.requestLimit < promises.length) {
        const results: Array<any> = await Promise.all(promises);
        // 非同期処理中に例外が発生していればresultの中にSystemerrorが格納されているのでチェックする
        for (const result of results) {
          if (result instanceof SystemError) {
            errorHandle(result, this);
          }
        }
        promises = [];
        await this.$timerUtil.setTimeout(1000);
      }
    }
    let results: Array<any> = await Promise.all(promises);
    for (const result of results) {
      if (result instanceof SystemError) {
        errorHandle(result, this);
      }
    }

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      let params: any = {};
      if (this.distributed != undefined) {
        params["distributed"] = this.distributed;
      }
      const count: number = await this.picturesService.getCount(userStore, params);

      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 1ページ分の写真を作成する
   */
  public async getPictures(): Promise<Array<PicturesGetModel>> {
    const startIndex: number = this.currentRows;
    // tableのライブラリでソートやフィルタリング、ページングを管理するので表示対象のよりも多めに取得しておく
    const endIndex: number = this.perPage * (this.currentPage * this.pageRange);
    const params: any = {
      startIndex: startIndex,
      endIndex: endIndex,
      order: "desc"
    };
    if (this.distributed != undefined) {
      params["distributed"] = this.distributed;
    }

    const response: Array<PicturesGetModel> = await this.picturesService.gets(userStore, params);

    return response;
  }

  /**
   * ### 写真の行を作成する
   */
  public createPictureItems(pictures: Array<PicturesGetModel>, map: Map<number, ResidentGroupsGetModel>): Array<PictureTableItem> {
    let items: Array<PictureTableItem> = [];

    for (const picture of pictures) {
      const group: ResidentGroupsGetModel | undefined = map.get(picture.groupId);

      items.push({
        id: picture.id,
        distributedDatetime: picture.distributedDatetime ? this.convertDisplayDate(picture.distributedDatetime) : "",
        receiveDatetime: this.convertDisplayDate(picture.receiveDatetime),
        residentRoom: picture.residentRoom,
        residentName: picture.residentName,
        residentKanaName: picture.residentKanaName ? picture.residentKanaName : "",
        familyName: picture.familyName,
        imageUrl: picture.imageUrl,
        convertedImageUrl: "",
        comment: picture.comment ? picture.comment : "",
        groupId: picture.groupId,
        groupName: group ? group.name : ""
      });
    }

    return items;
  }

  /**
   * ### 非同期で画像URLを変換する
   */
  public convertImage(no: number): Promise<void> {
    return new Promise(async (resolve: Function, reject: Function) => {
      try {
        const imageUrl: string = this.pictureItems[no].imageUrl;
        const convertedImageUrl: string = await this.convertImageUrl(imageUrl);

        this.pictureItems[no].convertedImageUrl = convertedImageUrl;
        this.$forceUpdate();
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * ### DBに保存されているS3URLを公開状態にし、公開状態にした画像のURLを返却する
   */
  public async convertImageUrl(imageUrl: string): Promise<string> {
    if (!imageUrl) {
      return "error";
    }

    // 画像URL取得
    const convertImageUrl = await this.s3UrlService.get(userStore, Setting.backets.imageExpireHour, imageUrl);

    return convertImageUrl;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.pictureItems = [];
    this.currentRows = this.pictureItems.length;
    await this.createView();

    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;

    // 初期表示時点では一定数しか情報を取得していないので、既に情報が取得されていなければ取得する
    const newPageRows = this.perPage * (this.currentPage - 1);
    if (this.currentRows <= newPageRows) {
      await this.createView();
    }

    return;
  }

  /**
   * ### 画面表示用の日付に変換する
   *
   * - 渡される日付がnullの場合は空文字を返す
   */
  public convertDisplayDate(date: number): string {
    if (!date) {
      return "";
    }
    const convertedDate: string = this.$dateUtil.convertNumberToString(date, "mm月dd日 hh:MM");
    return convertedDate;
  }

  /**
   * ### 写真をモーダルで表示する
   */
  public showPicture(picture: PictureTableItem): void {
    this.showImageUrl = picture.convertedImageUrl;

    const imageModal: ImageModal | Vue | Element | Vue[] | Element[] = this.$refs.imageModal;
    if (imageModal instanceof ImageModal) {
      imageModal.show();
    }

    return;
  }

  /**
   * ### 写真の表示を親コンポーネントに伝える
   */
  public print(picture: PictureTableItem): void {
    this.$emit("print", picture);

    return;
  }

  /**
   * ### 写真の配布を親コンポーネントに伝える
   */
  public distribute(picture: PictureTableItem): void {
    this.$emit("distribute", picture);

    return;
  }

  /**
   * ### フィルタリング
   */
  public filterTable(row: PictureTableItem, filter: FilterSetting): boolean {
    return this.filterGroupId(row, filter) && this.filterSearchWords(row, filter);
  }

  public filterGroupId(row: PictureTableItem, filter: FilterSetting): boolean {
    if (filter.groupId == "") {
      return true;
    }

    if (row.groupId == Number(filter.groupId)) {
      return true;
    } else {
      return false;
    }
  }

  public filterSearchWords(row: PictureTableItem, filter: FilterSetting): boolean {
    if (filter.searchWord == "") {
      return true;
    }

    const words: string[] = this.$filterUtil.createSearchWord(filter.searchWord);

    let foundRoomName: boolean = true;
    let foundResidentName: boolean = true;
    let foundResidentKanaName: boolean = true;
    let foundFamilyName: boolean = true;

    for (const word of words) {
      // 部屋名
      if (row.residentRoom.indexOf(word) == -1) {
        foundRoomName = false;
      }

      // 利用者名（ふりがな）
      if (row.residentKanaName) {
        if (row.residentKanaName.indexOf(word) == -1) {
          foundResidentKanaName = false;
        }
      } else {
        foundResidentKanaName = false;
      }

      // 利用者名
      if (row.residentName.indexOf(word) == -1) {
        foundResidentName = false;
      }

      // 家族名
      if (row.familyName.indexOf(word) == -1) {
        foundFamilyName = false;
      }
    }

    return foundRoomName || foundResidentName || foundResidentKanaName || foundFamilyName;
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   * ### 画面の作り直し
   */
  public async refresh(): Promise<void> {
    this.currentPage = 1;
    this.pictureItems = [];
    this.currentRows = this.pictureItems.length;
    await this.createView();

    return;
  }

  setFilterSetting() {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name + `-${this.settingNameSuffix}`, filter: this.filter });
    }
    return;
  }
}

/** テーブルの列名の情報 */
export interface PictureTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface PictureTableItem {
  id: number;
  distributedDatetime: string;
  receiveDatetime: string;
  residentName: string;
  residentKanaName: string;
  residentRoom: string;
  familyName: string;
  imageUrl: string;
  convertedImageUrl: string;
  comment: string;
  groupId: number;
  groupName: string;
}

export interface FilterSetting {
  groupId: string;
  searchWord: string;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

.picture-table-component {
  .distribute-btn {
    color: $warning-btn-font-color !important;
  }

  .distributed-text {
    @include bold-font;
    color: $font-disable-font-color;
  }
}
</style>
